/**
 * Customer picker shared rendering helpers.
 *
 * Centralises the dropdown row rendering for every customer picker in
 * the office app (calendar filter, list page filters, job/quote
 * creation forms, quick-create modal). Single source of truth for:
 *
 *   - status badges ("En pause" / "Inactif") next to the customer name
 *   - the ``isCustomerDisabled`` predicate used by *creation* pickers
 *     to refuse selection of non-active customers (the badge alone
 *     conveys the disabled state — no extra tooltip is rendered, by
 *     explicit product decision: badges + faded row are enough and
 *     hover tooltips were judged noisy on busy lists).
 *
 * Why centralise: prior to this module each consumer page (visits.js,
 * jobs.js, quotes.js, invoices.js, credit-notes.js, credit-balances.js,
 * customers.js) carried an identical copy of ``renderCustomerItem``.
 * The 7 copies diverged subtly over time (some had ``truncate``, some
 * did not) and none knew about non-active statuses. Adding badges
 * required either editing all 7 OR routing through a shared helper —
 * we picked the helper.
 *
 * Translation strings are read from a ``<script id="customer-picker-i18n"
 * type="application/json">`` block injected by ``base_office.html`` (Phase 21.D
 * migration — used to be a plain inline ``window.CUSTOMER_PICKER_I18N = {…}``
 * assignment). For backwards compatibility with consumers that read the
 * global directly (``schedule-calendar-filters.js``, ``schedule-quick-create.js``,
 * ``job-form.js``), the parsed dict is also published on
 * ``window.CUSTOMER_PICKER_I18N`` on first lookup. Falls back to English if
 * neither source is available.
 */

import { escapeHtml } from '../helpers.js';
import { customerAvatarHtml } from './customer-avatar.js';

const I18N_SCRIPT_ID = 'customer-picker-i18n';

/**
 * Picker mode — drives the disabled / selectable behaviour:
 *   - 'filter' : every status is selectable (the office may want to
 *     filter past visits of an on-hold or inactive customer).
 *   - 'create' : non-active customers render visually faded + the
 *     ``isCustomerDisabled`` predicate returns true, so SearchPicker
 *     will refuse selection. The status badge next to the name is the
 *     only hint shown — no hover tooltip.
 */
export const PICKER_MODE_FILTER = 'filter';
export const PICKER_MODE_CREATE = 'create';

const STATUS_ACTIVE = 'active';
const STATUS_ON_HOLD = 'on_hold';
const STATUS_INACTIVE = 'inactive';

function readI18nScript() {
  if (typeof document === 'undefined') return null;
  const el = document.getElementById(I18N_SCRIPT_ID);
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch {
    return null;
  }
}

/**
 * Hydrate ``window.CUSTOMER_PICKER_I18N`` from the
 * ``<script id="customer-picker-i18n" type="application/json">`` block
 * that ``base_office.html`` injects on every office page (Phase 21.D).
 *
 * Idempotent: a no-op if the global is already populated. Safe to call
 * from multiple places — typically once from ``app.js``'s
 * ``DOMContentLoaded`` handler (so other consumers that read
 * ``window.CUSTOMER_PICKER_I18N`` directly — ``job-form.js``,
 * ``schedule-calendar-filters.js``, ``schedule-quick-create.js`` —
 * see a populated dict before their first interaction handler fires).
 */
export function hydrateCustomerPickerI18n() {
  if (typeof window === 'undefined') return;
  if (window.CUSTOMER_PICKER_I18N) return;
  const fromScript = readI18nScript();
  if (fromScript) {
    window.CUSTOMER_PICKER_I18N = fromScript;
  }
}

function getI18nDict() {
  if (typeof window === 'undefined') return {};
  if (window.CUSTOMER_PICKER_I18N) return window.CUSTOMER_PICKER_I18N;
  const fromScript = readI18nScript();
  if (fromScript) {
    window.CUSTOMER_PICKER_I18N = fromScript;
    return fromScript;
  }
  return {};
}

function t(key, fallback) {
  const dict = getI18nDict();
  return dict[key] || fallback;
}

/**
 * True when the picker should refuse selection of this customer.
 * Used as the SearchPicker ``isItemDisabled`` callback.
 */
export function isCustomerDisabled(item, mode = PICKER_MODE_FILTER) {
  if (mode !== PICKER_MODE_CREATE) return false;
  return item && item.status && item.status !== STATUS_ACTIVE;
}

/**
 * Status badge HTML next to the customer name. Returns empty string
 * for active (no badge needed). The badge shape mirrors the office
 * ``badge-orange`` / ``badge-gray`` Tailwind utilities so it visually
 * matches the customers list page.
 */
function renderStatusBadge(status) {
  if (!status || status === STATUS_ACTIVE) return '';
  if (status === STATUS_ON_HOLD) {
    return (
      '<span class="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium '
      + 'rounded bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">'
      + escapeHtml(t('on_hold', 'On hold'))
      + '</span>'
    );
  }
  if (status === STATUS_INACTIVE) {
    // Match the customers list ``badge-red`` (see ``frontend/src/app.css``):
    // ``bg-red-100 text-red-800`` light / ``bg-red-900 text-red-300`` dark.
    // Same colour everywhere a paused-customer-as-final-state is surfaced
    // so the operator immediately associates the red tag with "service
    // ended" instead of confusing it with a neutral "no info" grey.
    return (
      '<span class="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium '
      + 'rounded bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">'
      + escapeHtml(t('inactive', 'Inactive'))
      + '</span>'
    );
  }
  return '';
}

/**
 * Build the inner HTML for one customer row in the picker dropdown.
 *
 * Layout is intentionally identical to the canonical calendar picker
 * (``templates/office/schedule/_calendar_scripts.html`` — the inline
 * ``searchCustomersAPI`` renderer). Concretely: ``flex items-center``
 * with the avatar, the name ``<span>`` and the status badge as
 * **three direct flex siblings** (not name+badge inside a nested
 * ``flex-1 min-w-0`` wrapper).
 *
 * Why three direct siblings matters — the previous implementation
 * wrapped the name and badge inside an inner ``flex-1 min-w-0`` div,
 * which made them inline content of that wrapper rather than flex
 * items. The wrapper width depended on container layout passes, and
 * in narrow filter columns this produced a visible flicker:
 *
 *   - first paint  : wrapper still narrow → name "ABC Services" wraps
 *                    to two lines, the inline-flex badge tucks at the
 *                    end of the second line ("calendar look")
 *   - second pass  : wrapper widens just past the name's intrinsic
 *                    width → name fits on one line, but
 *                    "ABC Services" + "En attente" still overflows so
 *                    the badge alone wraps below ("ugly look")
 *
 * Putting the name and badge directly in the parent flex row removes
 * the nested wrapper entirely: the name span becomes a flex item with
 * default ``flex-shrink: 1`` and shrinks-to-fit (its text wrapping
 * naturally), and the badge stays at its content size right next to
 * the last word — matching the calendar in every viewport width.
 *
 * The ``truncate`` option remains for the edge case where vertical
 * growth is unacceptable (rare); it adds ``truncate`` (single-line +
 * ellipsis) on the name span. ``mode`` is still accepted for
 * symmetry with ``isCustomerDisabled`` even though the row HTML is
 * identical in both modes — the disabled visual state (opacity,
 * cursor) is applied by the caller (SearchPicker / job-form /
 * quick-create) on the wrapper element, not here.
 *
 * @param {object} item - ``{id, name, status}`` from ``api_customers_search``
 *                        (or the inline ``customers_json`` for forms).
 * @param {object} [options]
 * @param {string} [options.mode='filter']  - see ``PICKER_MODE_*``
 * @param {boolean} [options.truncate=false]
 * @returns {string} HTML to embed inside the ``.search-picker-item`` wrapper.
 */
export function renderCustomerItem(item, options = {}) {
  const truncate = options.truncate === true;
  const name = item.name || item.display_name || '';
  const escName = escapeHtml(name);
  const badge = renderStatusBadge(item.status);
  const nameClass = truncate
    ? 'text-sm font-medium text-gray-900 dark:text-white truncate min-w-0'
    : 'text-sm font-medium text-gray-900 dark:text-white min-w-0';
  return (
    '<div class="flex items-center px-3 py-2">'
    + customerAvatarHtml(name, item.customer_type)
    + `<span class="${nameClass}">${escName}</span>`
    + badge
    + '</div>'
  );
}
